import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { CartModifierGroup } from '../utils/cart';
import { TopLevelMenuItem } from '../utils/menu';
import { GenericMap } from '../utils/types';
import { getModSymbolFromInput } from '../utils/autocomplete';
import { convertToMap, convertToMapUsingName } from '../utils';
import { ParsedMenuItem, ParsedModifierGroup } from '../types/menu';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { canShowBMItem } from '../components/ItemBuilder/menu/modGroup.utils';
import {
  hasExpressModePeriods,
  isExpressModeActive,
} from '../utils/expressMode';
import {
  checkMealPeriodAvailabilityAndActive,
  isConstraintApplicableToRestaurantGroups,
  getRestaurantGroups,
} from '../utils/menuUnification.utils';
import {
  selectActiveRestaurantCode,
  selectActiveRestaurantTimezone,
  selectPrimaryRestaurantCode,
} from './restaurant';
import {
  ALREADY_SPOKEN_TOTAL_READBACK_CONSTRAINT_PREFIX,
  TOTAL_READBACK_CONSTRAINT_PREFIX,
} from '../constants';
import {
  selectFeatureFlagDoNotUpsellInExpressMode,
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
} from '../redux/features/config/config.selector';
import { selectIsExpressTimerElapsed } from '../redux/features/taskRouter/taskRouter.selector';
import { selectWithheldConstraints } from '../redux/features/experiments/experiments.selector';
import { isConstraintWithheld } from '../redux/features/experiments/experiments.utils';

const selectMenu = (state: RootState) => state.menu;

export function getModSymbolMapping(state: RootState) {
  return state.menu.modSymbolMapping;
}

export function getCodeNameMapping(state: RootState) {
  return state.menu.codeNameMapping;
}

export function generateMenuItemsSelector(currentMessage: string) {
  return (state: RootState): (ParsedMenuItem | TopLevelMenuItem)[] => {
    const {
      cart: { cartItems },
      dialog: {
        selectedItem: dialogSelectedItem,
        selectedItemCartId,
        selectedModGroup: dialogSelectedModGroup,
      },
      menu: { modSymbolMapping, topLevelMenuItems, unavailableItemsByName },
    } = state;
    const cartItemsValues = Object.values(cartItems);
    const selectedItem =
      dialogSelectedItem || cartItemsValues[cartItemsValues.length - 1];
    // A '/upsell' entry withheld for this session's experiment group is not offered.
    const withheldConstraints = selectWithheldConstraints(state);
    let currentItems: (ParsedMenuItem | TopLevelMenuItem)[] = Object.values(
      topLevelMenuItems
    ).filter(
      (item) =>
        !item.name?.includes('/upsell') ||
        !isConstraintWithheld(withheldConstraints, item.activeConstraintName)
    );

    let currentMods: (ParsedMenuItem | TopLevelMenuItem)[] = [];
    const isUnifiedMenu = getMenuUnificationFeatureFlag();

    if (selectedItem) {
      let modGroups: ParsedModifierGroup[] = [];
      let childrenMods: GenericMap<CartModifierGroup>;
      if (dialogSelectedModGroup) {
        modGroups = [dialogSelectedModGroup];
        const inChildModGroups =
          cartItems[selectedItemCartId]?.childModifierGroups[
            dialogSelectedModGroup?.id || ''
          ];
        childrenMods = inChildModGroups
          ? convertToMap([Object.assign(inChildModGroups)])
          : {};
      } else {
        modGroups = Object.values(selectedItem.modifierGroups);
        childrenMods = Object.assign(
          cartItems[selectedItemCartId]?.childModifierGroups || {}
        );
      }
      recurviselyGetMods(childrenMods, modGroups);

      const updatedCartItem = {
        ...selectedItem,
        childModifierGroups: childrenMods,
      };
      modGroups.forEach((modGroup) => {
        const modGroupMenuItems = Object.values(modGroup.menuItems).filter(
          (menuItem) => {
            if (isUnifiedMenu) {
              return canShowBMItem({
                unavailableItemsByName,
                itemName: menuItem.name,
                modGroupName: modGroup.name,
                cartItem: updatedCartItem,
              });
            } else {
              return true;
            }
          }
        );
        currentMods = [...currentMods, ...modGroupMenuItems];
        currentItems = [...currentItems, ...modGroupMenuItems];
      });
    }

    const modSymbol = getModSymbolFromInput(currentMessage, modSymbolMapping);
    let itemsList = modSymbol ? currentMods : currentItems;

    return itemsList;
  };
}
function recurviselyGetMods(
  childrenMods: GenericMap<CartModifierGroup>,
  modGroups: ParsedModifierGroup[]
) {
  const children = Object.values(childrenMods || {})
    .flatMap((child) =>
      Object.values(child.selectedItems).sort(
        (itemA, itemB) =>
          (itemA.parentModifierGroupIndex || 0) -
          (itemB.parentModifierGroupIndex || 0)
      )
    )
    .flatMap(({ modifierGroups, childModifierGroups }) => {
      recurviselyGetMods(childModifierGroups, modGroups);
      return Object.values(modifierGroups);
    });
  modGroups.push(...children);
}

export function selectPersistentVoiceProps(state: RootState) {
  return state.menu.persistentVoiceProps;
}

export const selectMenuRes = createSelector(selectMenu, (menu) => menu.menuRes);

export const selectAvailableCategoryWithTimePeriod = createSelector(
  selectMenu,
  (menu) => menu.availableCategoryWithTimePeriod
);

export const selectTopLevelMenuItems = createSelector(
  selectMenu,
  (menu) => menu.topLevelMenuItems
);

export const promptsSelector = createSelector(
  selectTopLevelMenuItems,
  (items) => {
    const menuPrompts = Object.values(items).reduce((acc, item) => {
      if (item.name && item.name.startsWith('/')) {
        const [intent, ...phraseParts] = item.name.split(':');
        const phrase = phraseParts.join(':').trim();
        if (phrase) {
          acc.push({
            intent: intent.slice(1),
            phrase: phrase,
          });
        }
      }
      return acc;
    }, [] as Array<{ intent: string; phrase: string }>);

    return menuPrompts.reduce((acc, item) => {
      acc[item.intent] = item;
      return acc;
    }, {} as Record<string, { intent: string; phrase: string }>);
  }
);

export const selectedMenuVersionSelector = createSelector(
  selectMenu,
  (menu) => menu.selectedMenuVersion
);
export const menuVersionsSelector = createSelector(
  selectMenu,
  (menu) => menu.menuVersions
);
export const prodLiveVersionSelector = createSelector(
  selectMenu,
  (menu) => menu.prodLiveVersion
);

export const posSettingsSelector = createSelector(
  selectMenu,
  (menu) => menu.menuRes?.posSettings || {}
);

export const modSymbolMappingSelector = createSelector(
  selectMenu,
  (menu) => menu.modSymbolMapping
);

export const selectIsBrandMenu = createSelector(
  selectMenu,
  (menu) => !!menu.brandMenu
);

export const selectUnavailableItemsByName = createSelector(
  selectMenu,
  (menu) => menu.unavailableItemsByName
);

export const selectBMItemsSynonyms = createSelector(
  selectMenu,
  (menu) => menu.bmItemsSynonyms
);

export const selectLoadedVersionDetails = createSelector(
  selectMenu,
  (menu) => menu.loadedVersionDetails
);

export const selectBrandMenuVersion = createSelector(
  selectLoadedVersionDetails,
  (loadedVersionDetails) => loadedVersionDetails?.brandMenuVersion
);

export const selectBrandMenu = createSelector(
  selectMenu,
  (menu) => menu.brandMenu
);

export const selectBrandMenuItemsByName = createSelector(
  selectMenu,
  (menu) => menu.brandMenuItemsByName
);

export const selectItemConversionBusinessRules = createSelector(
  selectMenu,
  (menu) => menu.itemConversionBusinessRules
);

export const selectBrandMenuConstraints = createSelector(
  selectBrandMenu,
  (brandMenu) => convertToMapUsingName(brandMenu?.constraints || [])
);

export const selectRestaurantMealPeriodsByName = createSelector(
  selectMenu,
  (menu) => menu.restaurantMealPeriodsByName
);

export const selectIsExpressModeActive = createSelector(
  selectRestaurantMealPeriodsByName,
  selectActiveRestaurantTimezone,
  (restaurantMealPeriodsByName, timezone) =>
    isExpressModeActive(restaurantMealPeriodsByName, timezone)
);

export const selectHasExpressModeMealPeriods = createSelector(
  selectRestaurantMealPeriodsByName,
  hasExpressModePeriods
);

export const selectIsExpressUpsellMode = createSelector(
  selectIsExpressModeActive,
  selectFeatureFlagDoNotUpsellInExpressMode,
  (isActive, ffEnabled) => isActive && ffEnabled
);

// Time-based express mode: hitl_3_0 + hitl_express_timer on AND the order has
// elapsed past max_seconds (latched in taskRouter). Enters express mode, which
// suppresses only `do_not_upsell_in_express_mode`-tagged upsells.
export const selectIsExpressTimerActive = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectIsExpressTimerElapsed,
  (hitl3, expressTimerEnabled, elapsed) =>
    hitl3 && expressTimerEnabled && elapsed
);

// Express mode has two independent triggers and either one suppresses the
// `do_not_upsell_in_express_mode`-tagged upsells: an active express meal
// period (behind its flag) or the elapsed order timer. Anything deciding
// "may this prompt be asked?" must read both — reading one half is what let
// the timer path diverge from the meal-period path (HITL-1529).
export const selectIsExpressUpsellSuppressionActive = createSelector(
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
  (isExpressUpsellMode, isExpressTimerActive) =>
    isExpressUpsellMode || isExpressTimerActive
);

// Shared resolver for the Complete-prompt constraint families (`total_readback`
// and `already_spoken_total_readback`): find the active constraint with the
// given name prefix and return its prompt, or null when none is usable.
const findTotalReadbackPromptByPrefix = ({
  constraints,
  timezone,
  restaurantMealPeriodsByName,
  brandMenu,
  restaurantCode,
  primaryRestaurantCode,
  prefix,
}: {
  constraints: ReturnType<typeof selectBrandMenuConstraints>;
  timezone: ReturnType<typeof selectActiveRestaurantTimezone>;
  restaurantMealPeriodsByName: ReturnType<
    typeof selectRestaurantMealPeriodsByName
  >;
  brandMenu: ReturnType<typeof selectBrandMenu>;
  restaurantCode: ReturnType<typeof selectActiveRestaurantCode>;
  primaryRestaurantCode: ReturnType<typeof selectPrimaryRestaurantCode>;
  prefix: string;
}): string | null => {
  // Find all constraints that start with the given prefix
  const totalReadbackConstraints = Object.keys(constraints || {}).filter(
    (key) => key.startsWith(prefix)
  );

  // Use primaryRestaurantCode for brand menu group lookups — in mirroring
  // scenarios the selected code differs from the code in restaurant_groups.
  const currentRestaurantGroups = getRestaurantGroups(
    primaryRestaurantCode || restaurantCode || '',
    brandMenu?.restaurant_groups || []
  );

  // A usable constraint has a prompt, an active meal period (or none), and
  // applies to this restaurant's groups.
  const isUsable = (constraintName: string) => {
    const constraint = constraints[constraintName];
    if (!constraint?.prompt) {
      return false;
    }

    const isAvailableAndActive = checkMealPeriodAvailabilityAndActive({
      mealPeriod: constraint.meal_period || null,
      timezone,
      restaurantMealPeriodsByName,
      entityName: constraintName,
    });

    if (!isAvailableAndActive) {
      return false;
    }

    return isConstraintApplicableToRestaurantGroups({
      constraint,
      restaurantMealPeriodsByName: restaurantMealPeriodsByName || {},
      currentRestaurantGroups,
    });
  };

  // A menu can carry both a meal-period-specific readback (e.g.
  // total_readback_dinner_rush) and an all-day one with no meal period. The
  // all-day constraint is always available, so first-match-wins would let it
  // shadow the specific prompt. Prefer the specific one; fall back to the
  // first all-day constraint only when no meal-period-specific one is active.
  const hasMealPeriod = (constraintName: string) =>
    Boolean(constraints[constraintName]?.meal_period);

  const winner =
    totalReadbackConstraints.filter(hasMealPeriod).find(isUsable) ??
    totalReadbackConstraints
      .filter((name) => !hasMealPeriod(name))
      .find(isUsable);

  return winner ? constraints[winner].prompt : null;
};

export const selectTotalConstraint = createSelector(
  selectBrandMenuConstraints,
  selectActiveRestaurantTimezone,
  selectRestaurantMealPeriodsByName,
  selectBrandMenu,
  selectActiveRestaurantCode,
  selectPrimaryRestaurantCode,
  (
    constraints,
    timezone,
    restaurantMealPeriodsByName,
    brandMenu,
    restaurantCode,
    primaryRestaurantCode
  ) => {
    const prompt = findTotalReadbackPromptByPrefix({
      constraints,
      timezone,
      restaurantMealPeriodsByName,
      brandMenu,
      restaurantCode,
      primaryRestaurantCode,
      prefix: TOTAL_READBACK_CONSTRAINT_PREFIX,
    });

    if (!prompt) {
      return null;
    }

    return {
      'total-template': {
        intent: 'total-template',
        phrase: prompt,
      },
    };
  }
);

// HITL-1570: the total-less Complete prompt for when the donation upsell
// already spoke the check total. Same resolution rules as
// selectTotalConstraint, over the `already_spoken_total_readback` family
// (which the `total_readback` prefix match above never picks up). Returns the
// prompt string, or null when the menu doesn't define a usable one.
export const selectAlreadySpokenTotalConstraint = createSelector(
  selectBrandMenuConstraints,
  selectActiveRestaurantTimezone,
  selectRestaurantMealPeriodsByName,
  selectBrandMenu,
  selectActiveRestaurantCode,
  selectPrimaryRestaurantCode,
  (
    constraints,
    timezone,
    restaurantMealPeriodsByName,
    brandMenu,
    restaurantCode,
    primaryRestaurantCode
  ) =>
    findTotalReadbackPromptByPrefix({
      constraints,
      timezone,
      restaurantMealPeriodsByName,
      brandMenu,
      restaurantCode,
      primaryRestaurantCode,
      prefix: ALREADY_SPOKEN_TOTAL_READBACK_CONSTRAINT_PREFIX,
    })
);

export const selectGreetingDetails = createSelector(
  selectMenu,
  (menu) => menu.greetingDetails
);

export const selectIntentCards = createSelector(
  selectMenu,
  (menu) => menu.intentCards
);

export const selectDismissReadbackPhrases = createSelector(
  selectMenu,
  (menu) => menu.dismissReadbackPhrases
);
